import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCompany } from "../../context/CompanyContext";
import { getAccounts } from "../services/crmAccountsService";
import {
  createContact,
  getContacts,
  updateContact,
} from "../services/crmContactsService";
import "../styles/crm.css";

const defaultForm = {
  account_id: "",
  full_name: "",
  job_title: "",
  email: "",
  phone: "",
  preferred_channel: "",
  is_primary: false,
  decision_maker: false,
};

const ContactsPage = () => {
  const { companyId } = useCompany();
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [search, setSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [primaryFilter, setPrimaryFilter] = useState("all");
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setMessage("");

    try {
      const [contactRows, accountRows] = await Promise.all([
        getContacts(companyId),
        getAccounts(companyId),
      ]);

      setContacts(contactRows);
      setAccounts(accountRows);
    } catch (error) {
      setMessage(error.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFormChange = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm(defaultForm);
    setEditingId("");
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingId(row.id);
    setForm({
      account_id: row.account_id || "",
      full_name: row.full_name || "",
      job_title: row.job_title || "",
      email: row.email || "",
      phone: row.phone || "",
      preferred_channel: row.preferred_channel || "",
      is_primary: Boolean(row.is_primary),
      decision_maker: Boolean(row.decision_maker),
    });
    setMessage("");
  }, []);

  const handleSave = useCallback(async () => {
    if (!companyId || !form.account_id || !form.full_name.trim()) return;

    setSaving(true);
    setMessage("");

    try {
      const payload = {
        company_id: companyId,
        account_id: form.account_id,
        full_name: form.full_name.trim(),
        job_title: form.job_title.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        preferred_channel: form.preferred_channel.trim() || null,
        is_primary: form.is_primary,
        decision_maker: form.decision_maker,
      };

      if (editingId) {
        await updateContact(editingId, companyId, payload);
      } else {
        await createContact(payload);
      }

      resetForm();
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  }, [companyId, form, editingId, load, resetForm]);

  const filteredContacts = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return contacts.filter((row) => {
      const matchesSearch =
        !searchValue ||
        String(row.full_name || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.email || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.phone || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.job_title || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.crm_accounts?.name || "")
          .toLowerCase()
          .includes(searchValue);

      const matchesAccount =
        accountFilter === "all" ||
        String(row.account_id || "") === accountFilter;

      const matchesPrimary =
        primaryFilter === "all" ||
        (primaryFilter === "yes" && row.is_primary) ||
        (primaryFilter === "no" && !row.is_primary);

      return matchesSearch && matchesAccount && matchesPrimary;
    });
  }, [contacts, search, accountFilter, primaryFilter]);

  return (
    <div>
      <h2 className="crm-page-title">Contacts</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          {editingId ? "Edit Contact" : "Create Contact"}
        </h3>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Account</label>
            <select
              className="crm-select"
              value={form.account_id}
              onChange={(event) =>
                handleFormChange("account_id", event.target.value)
              }
            >
              <option value="">Select account</option>
              {accounts.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>

          <div className="crm-form-group">
            <label>Full Name</label>
            <input
              className="crm-input"
              value={form.full_name}
              onChange={(event) =>
                handleFormChange("full_name", event.target.value)
              }
              placeholder="Enter full name"
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Job Title</label>
            <input
              className="crm-input"
              value={form.job_title}
              onChange={(event) =>
                handleFormChange("job_title", event.target.value)
              }
              placeholder="Enter job title"
            />
          </div>

          <div className="crm-form-group">
            <label>Email</label>
            <input
              className="crm-input"
              value={form.email}
              onChange={(event) =>
                handleFormChange("email", event.target.value)
              }
              placeholder="Enter email"
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Phone</label>
            <input
              className="crm-input"
              value={form.phone}
              onChange={(event) =>
                handleFormChange("phone", event.target.value)
              }
              placeholder="Enter phone"
            />
          </div>

          <div className="crm-form-group">
            <label>Preferred Channel</label>
            <input
              className="crm-input"
              value={form.preferred_channel}
              onChange={(event) =>
                handleFormChange("preferred_channel", event.target.value)
              }
              placeholder="Email, Phone, WhatsApp"
            />
          </div>
        </div>

        <div className="crm-inline-actions" style={{ marginBottom: "16px" }}>
          <label className="crm-checkbox-row">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(event) =>
                handleFormChange("is_primary", event.target.checked)
              }
            />
            <span>Primary Contact</span>
          </label>

          <label className="crm-checkbox-row">
            <input
              type="checkbox"
              checked={form.decision_maker}
              onChange={(event) =>
                handleFormChange("decision_maker", event.target.checked)
              }
            />
            <span>Decision Maker</span>
          </label>
        </div>

        <div className="crm-inline-actions">
          <button
            className="crm-primary-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : editingId
              ? "Update Contact"
              : "Add Contact"}
          </button>

          {editingId ? (
            <button
              className="crm-secondary-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <div className="crm-filter-row">
          <div className="crm-form-group">
            <label>Search</label>
            <input
              className="crm-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search contacts"
            />
          </div>

          <div className="crm-form-group">
            <label>Account</label>
            <select
              className="crm-select"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
            >
              <option value="all">All</option>
              {accounts.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>

          <div className="crm-form-group">
            <label>Primary Contact</label>
            <select
              className="crm-select"
              value={primaryFilter}
              onChange={(event) => setPrimaryFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="crm-message">Loading contacts...</div>
      ) : filteredContacts.length === 0 ? (
        <div className="crm-message">No contacts found.</div>
      ) : (
        <div className="crm-table-card">
          <div className="crm-data-table-wrap">
            <table className="crm-data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Account</th>
                  <th>Job Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Primary</th>
                  <th>Decision Maker</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((row) => (
                  <tr key={row.id}>
                    <td>{row.full_name || "N/A"}</td>
                    <td>{row.crm_accounts?.name || "N/A"}</td>
                    <td>{row.job_title || "N/A"}</td>
                    <td>{row.email || "N/A"}</td>
                    <td>{row.phone || "N/A"}</td>
                    <td>{row.is_primary ? "Yes" : "No"}</td>
                    <td>{row.decision_maker ? "Yes" : "No"}</td>
                    <td>
                      <button
                        className="crm-small-btn alt"
                        onClick={() => handleEdit(row)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
